import { useState } from 'react';
import type { FormValues, FormHolidayValue, Holidays } from '../../../../../../common/types';
import { getFormHolidayValuesById } from '../../../../../../common/ui/calendar-utils';
import { sortHolidays } from '../../../../../../common/utils/calendars/utils';
import { useUid } from '../../../../../../common/utils/uid/index.tsx';
import { useFormContext } from '../../../../../../controllers/form/index.tsx';

export const useHolidaysState = (list: Holidays = [], getFormValues: () => FormValues) => {
	const [getUid] = useUid();

	const [, { setFormStatusToDirty }] = useFormContext();

	const [holidays, setHolidays] = useState<FormHolidayValue[]>(() => {
		const modifiedHolidayList = sortHolidays(list);

		return modifiedHolidayList.map((holiday) => ({
			...holiday,
			fieldId: getUid(),
			isNew: false,
		}));
	});

	const onRemove = (id: number) => {
		setHolidays(holidays.filter(({ fieldId }) => fieldId !== id));
		setFormStatusToDirty();
	};

	const onAdd = () => {
		setHolidays([
			...holidays,
			{ name: '', iso8601Date: '', isRecurring: false, isNew: true, fieldId: getUid() },
		]);
		setFormStatusToDirty();
	};

	const onSave = (id: number) => {
		const index = holidays.findIndex(({ fieldId }) => fieldId === id);
		const modifiedHoliday = getFormHolidayValuesById(id, getFormValues());
		const clonedHolidays = [...holidays];
		clonedHolidays.splice(index, 1, modifiedHoliday);
		setHolidays(clonedHolidays);
	};

	const onImport = (importedHolidays: FormHolidayValue[]) => {
		const modifiedImportedHolidays = importedHolidays.map((holiday) => ({
			...holiday,
			fieldId: getUid(),
		}));
		setHolidays([...holidays, ...modifiedImportedHolidays]);
		setFormStatusToDirty();
	};

	return [
		{
			holidays,
		},
		{
			onAdd,
			onRemove,
			onSave,
			onImport,
		},
	] as const;
};
